/**
 * 3PL mode on Settings > Locations: the switch that makes an org a 3PL
 * (`organizations.is_3pl_provider`) without anyone touching SQL.
 *
 * Everything here lives in the page's section 2 slot (DESIGN.md section 19.2),
 * the place the 3PL section appears once the org is a 3PL, so turning the mode
 * on or off swaps one thing for another in the same spot:
 *
 *   - `ThreePlModeOffer`: a quiet panel for an org that is not a 3PL yet. It
 *     must never compete with "Your locations" above it: muted box, small
 *     title, one outline button.
 *   - `ThreePlModeNeedsLocation`: 3PL mode is on but the org owns no location,
 *     so there is no 3PL section to show (`render3PLSurface` needs one). Says
 *     so, and keeps the way back (Turn off).
 *   - `TurnOffThreePlModeButton`: the quiet way out, for the 3PL section
 *     header. Org-wide, so it sits once on the section, never on each
 *     per-location card.
 *   - `ThreePlModeDialog`: the one confirm for both directions. It owns the
 *     mutation, refreshes the role surfaces and the organization context
 *     before it closes (so the slot already shows the new state), and shows a
 *     refusal inside the dialog instead of a toast.
 *
 * Each slot heading carries `data-three-pl-heading` and `tabIndex={-1}`: the
 * trigger button is gone after a change, so the page lands focus on whatever
 * heading replaced it.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Building2, Loader2, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { NoteLine } from '@/components/shared/NoteLine';
import { useOrganization } from '@/contexts/OrganizationContext';
import { trpc } from '@/lib/trpc';
import { trpcErrorCode } from '@/lib/trpc-error-utils';
import { cn } from '@/lib/utils';

export type ThreePlDirection = 'on' | 'off';

/** Every section 2 slot heading carries `data-three-pl-heading`; the page focuses it after a change. */
export const THREE_PL_HEADING_SELECTOR = '[data-three-pl-heading]';

const SLOT_PANEL_CLASS =
  'flex flex-wrap items-center justify-between gap-x-4 gap-y-3 rounded-lg border bg-muted/30 px-4 py-3';
// A programmatic landing target, never a tab stop: no ring on focus.
const SLOT_HEADING_CLASS = 'text-sm font-medium outline-none';
const QUIET_BUTTON_CLASS = 'h-8 gap-1.5 px-3 text-xs';

interface OfferProps {
  onTurnOn: () => void;
}

/** Section 2 slot for an org that is not a 3PL: what 3PL mode is for, and the way in. */
export function ThreePlModeOffer({ onTurnOn }: OfferProps) {
  const { t } = useTranslation('settings');
  const headingId = useId();
  const bodyId = useId();
  return (
    <section aria-labelledby={headingId} className="border-t border-muted-foreground/20 pt-6 motion-safe:animate-fade-in">
      <div className={SLOT_PANEL_CLASS}>
        <div className="min-w-0 max-w-prose space-y-0.5">
          <h3 id={headingId} tabIndex={-1} data-three-pl-heading="" className={SLOT_HEADING_CLASS}>
            {t('locations.threePl.offer.title')}
          </h3>
          <p id={bodyId} className="text-sm text-muted-foreground">
            {t('locations.threePl.offer.body')}
          </p>
        </div>
        <Button variant="outline" className={QUIET_BUTTON_CLASS} aria-describedby={bodyId} onClick={onTurnOn}>
          <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
          {t('locations.threePl.offer.action')}
        </Button>
      </div>
    </section>
  );
}

interface NeedsLocationProps {
  onTurnOff: () => void;
}

/** Section 2 slot for a 3PL that owns no location yet: nothing to share until it adds one. */
export function ThreePlModeNeedsLocation({ onTurnOff }: NeedsLocationProps) {
  const { t } = useTranslation('settings');
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="border-t border-muted-foreground/20 pt-6 motion-safe:animate-fade-in">
      <div className={SLOT_PANEL_CLASS}>
        <div className="min-w-0 max-w-prose space-y-0.5">
          <h3 id={headingId} tabIndex={-1} data-three-pl-heading="" className={SLOT_HEADING_CLASS}>
            {t('locations.threePl.needsLocation.title')}
          </h3>
          <p className="text-sm text-muted-foreground">{t('locations.threePl.needsLocation.body')}</p>
        </div>
        <TurnOffThreePlModeButton onClick={onTurnOff} />
      </div>
    </section>
  );
}

interface TurnOffButtonProps {
  onClick: () => void;
  className?: string;
}

/** The quiet secondary way out of 3PL mode. Opens the confirm; never changes anything itself. */
export function TurnOffThreePlModeButton({ onClick, className }: TurnOffButtonProps) {
  const { t } = useTranslation('settings');
  return (
    <Button
      variant="ghost"
      className={cn(QUIET_BUTTON_CLASS, 'shrink-0 text-muted-foreground hover:text-foreground', className)}
      onClick={onClick}
    >
      {t('locations.threePl.turnOff.action')}
    </Button>
  );
}

type Failure = 'refused' | 'rateLimited' | 'failed';

function classifyFailure(error: unknown): Failure {
  const code = trpcErrorCode(error);
  // CONFLICT is the server's turn-off guard (clients still connected). Its
  // sentence is THREE_PL_MODE_OFF_REFUSED_MESSAGE on the backend; the twin in
  // the settings namespace is shown so a zh session reads it in zh.
  if (code === 'CONFLICT') return 'refused';
  if (code === 'TOO_MANY_REQUESTS') return 'rateLimited';
  return 'failed';
}

const FAILURE_KEY: Record<Failure, string> = {
  refused: 'locations.threePl.turnOff.refused',
  rateLimited: 'locations.threePl.rateLimited',
  failed: 'locations.threePl.failed',
};

interface DialogProps {
  /** The direction being confirmed; null when the dialog is closed. */
  direction: ThreePlDirection | null;
  /** The org owns no location: turning on will not show the 3PL section yet. */
  ownsNoLocation: boolean;
  onClose: () => void;
  /**
   * After a successful change the trigger is gone. Lands focus on what
   * replaced it; returns false when there was nothing to focus, so the
   * dialog's default focus return runs instead.
   */
  focusAfterChange: () => boolean;
}

/** The one confirm for turning 3PL mode on or off. */
export function ThreePlModeDialog({ direction, ownsNoLocation, onClose, focusAfterChange }: DialogProps) {
  const { t } = useTranslation('settings');
  const utils = trpc.useUtils();
  const { reloadOrganization } = useOrganization();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const changedRef = useRef(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  // The copy follows the last direction asked for, so the exit animation
  // keeps its words after `direction` goes back to null. Each fresh open
  // starts with no failure on screen.
  const [shown, setShown] = useState<ThreePlDirection>(direction ?? 'on');
  const [prevDirection, setPrevDirection] = useState<ThreePlDirection | null>(direction);
  if (direction !== prevDirection) {
    setPrevDirection(direction);
    if (direction) {
      setShown(direction);
      setFailure(null);
    }
  }

  const setMode = trpc.organization.set3plProvider.useMutation({
    onSuccess: async () => {
      // Close onto fresh data (DESIGN.md section 12.2): the slot under the
      // dialog must already show the new state when focus lands on it.
      await utils.locations.getOrgRoleSurfaces.invalidate();
      // The organization context feeds Client prices, the sidebar and the
      // Shopify nudge. Marked stale first so its imperative fetch can never
      // be answered from cache.
      await utils.organization.getCurrent.invalidate();
      reloadOrganization();
    },
  });
  const saving = setMode.isPending;

  // A refusal disables Confirm (asking again would be refused again), so
  // focus moves to Cancel instead of falling out of the dialog.
  useEffect(() => {
    if (failure === 'refused') cancelRef.current?.focus();
  }, [failure]);

  const confirm = () => {
    if (!direction || saving || failure === 'refused') return;
    setFailure(null);
    setMode.mutate(
      { enabled: direction === 'on' },
      {
        onSuccess: () => {
          changedRef.current = true;
          onClose();
        },
        onError: (error) => setFailure(classifyFailure(error)),
      },
    );
  };

  const onOpenChange = (open: boolean) => {
    // A change in flight finishes first; its outcome closes the dialog.
    if (!open && !saving) onClose();
  };

  const isOn = shown === 'on';
  const lines = isOn
    ? [t('locations.threePl.turnOn.cardAppears'), t('locations.threePl.turnOn.clientPricesAppears'), t('locations.threePl.nothingElse')]
    : [t('locations.threePl.turnOff.hidden'), t('locations.threePl.turnOff.pricesKept'), t('locations.threePl.nothingElse')];

  return (
    <AlertDialog open={direction !== null} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className="max-w-md"
        onCloseAutoFocus={(event) => {
          if (!changedRef.current) return;
          changedRef.current = false;
          if (focusAfterChange()) event.preventDefault();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isOn ? t('locations.threePl.turnOn.title') : t('locations.threePl.turnOff.title')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <ul className="list-disc space-y-1.5 pl-5 text-left">
                {lines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              {isOn && ownsNoLocation && (
                <NoteLine as="p" icon={MapPin}>
                  {t('locations.threePl.turnOn.noLocations')}
                </NoteLine>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {failure && (
          <p role="alert" className="flex items-start gap-1.5 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{t(FAILURE_KEY[failure])}</span>
          </p>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel ref={cancelRef} disabled={saving}>
            {t('locations.threePl.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            className={cn(
              'gap-1.5 motion-reduce:transition-none',
              isOn
                ? 'bg-threadheads-purple text-white hover:bg-threadheads-purple/90'
                // Reversible, so orange outline, never destructive red (section 13.6).
                : 'border border-orange-300 bg-background text-orange-700 hover:bg-orange-50 hover:text-orange-700',
            )}
            disabled={saving || failure === 'refused'}
            aria-busy={saving || undefined}
            onClick={(event) => {
              event.preventDefault();
              confirm();
            }}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
            {isOn
              ? saving
                ? t('locations.threePl.turnOn.pending')
                : t('locations.threePl.turnOn.confirm')
              : saving
                ? t('locations.threePl.turnOff.pending')
                : t('locations.threePl.turnOff.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
